import { useContext } from 'react';
import { FlexContext } from '../../../contexts/flexContext';
import { AppContext } from '../../../contexts/appContext';
import { IStructuredSnowplowEvent } from '../../useSnowplowTracker';
import useNavigation from '../../useNavigation';

export interface IFlexNavigateFinish {
  type: 'finish';
  relaunch: boolean;
  urlToOpen?: string;
  snowplowEvent?: IStructuredSnowplowEvent;
}

const useFlexNavigateFinish = () => {
  const { reinitializeApp } = useContext(AppContext);
  const flexContext = useContext(FlexContext);
  const navigation = useNavigation();

  const handleFlexNavigateFinish = (flexNavigate: IFlexNavigateFinish) => {
    // Relaunch the app if instructed.
    if (flexNavigate.relaunch && !flexNavigate.urlToOpen) {
      reinitializeApp();
      return;
    }

    // relaunch + urlToOpen: navigate the current tab to the target URL via a full-page
    // replace. This avoids the new-tab behaviour of flexContext.finish while still
    // landing on the correct URL (e.g. Vitality home after completing a deep-link booking).
    if (flexNavigate.relaunch && flexNavigate.urlToOpen) {
      try {
        const target = new URL(flexNavigate.urlToOpen, window.location.origin);
        if (target.protocol === 'http:' || target.protocol === 'https:') {
          window.location.replace(target.toString());
        } else {
          reinitializeApp(flexNavigate.urlToOpen);
        }
      } catch {
        reinitializeApp(flexNavigate.urlToOpen);
      }
      return;
    }

    // When a flex flow's START immediately returns a `finish`, this hook is invoked
    // from within FlexContextProvider itself (via startFlex). A component cannot read
    // its own context, so `useContext(FlexContext)` yields the empty default here and
    // `flexContext.finish` is undefined. In that case there is nothing on the stack to
    // pop back to (only the deep-link entry), so instead of popping we navigate FORWARD
    // to the target: redirectToPath maps e.g. `vitalitygp://book?…` to the standalone
    // `/book?…` route, leaving the sticky `/app-link/` URL behind (so its RedirectToView
    // can't re-seed the start view) and avoiding a full app reinit.
    if (!flexContext.finish) {
      if (flexNavigate.urlToOpen) {
        navigation.redirectToPath(flexNavigate.urlToOpen);
      } else {
        reinitializeApp();
      }
      return;
    }

    flexContext.finish(flexNavigate.urlToOpen);
  };

  return handleFlexNavigateFinish;
};

export default useFlexNavigateFinish;
